import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from "@angular/router";
import {Store} from "@ngrx/store";
import {DbStoreState} from "../store";
import * as fromStore from "../store";
import {first, switchMap} from "rxjs/operators";
import {combineLatest} from "rxjs";
import {clientRoles} from "../user/user-roles-actions";
import {Config} from "../config/config";
import {UserSubscription, UserSubscriptionType} from "../user/user-subscription";
import {User} from "../user/user";
import {Website} from "./website/website";
import {UserService} from "../user/user.service";
import {Injectable} from "@angular/core";
import {PluginConnectService} from "../shared/services/plugin-connect/plugin-connect.service";
import {TypeUtils} from "../shared/utils/type-utils";

const SKIP_ONBOARDING_ANSWERS = ['plugins', 'Other - other', 'Other - plugins', 'skip', 'booster'];

@Injectable()
export class WebsitesGuard implements CanActivate {

  constructor(
    private readonly _router: Router,
    private readonly _store: Store<DbStoreState>,
    private readonly _userService: UserService,
    private readonly _pluginConnectService: PluginConnectService,
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {

    return new Promise<boolean>((resolve, reject) => {
      combineLatest(
        this._store.select(fromStore.getUserLoaded),
        this._store.select(fromStore.getUserLocalStorageLoaded),
      )
        .pipe(
          first(([userLoaded, storageLoaded]) => !!userLoaded && !!storageLoaded),
          switchMap(() => combineLatest(
            this._store.select(fromStore.getUser),
            this._store.select(fromStore.getUserLocalStorage),
            this._store.select(fromStore.getUserSubscription),
            this._store.select(fromStore.getCurrentWorkspace)
          )),
          first()
        )
        .subscribe(([user, userLocalStorage, userSubscription, currWorkspace]) => {

          if (userSubscription) {
            resolve(true);
          }
          else {
            this._store.select(fromStore.getWebsitesLoaded)
              .pipe(
                first(loaded => loaded),
                switchMap(() => this._store.select(fromStore.getWebsites)),
                first()
              )
              .subscribe(websites => {
         /*       if (userSubscription && this._trialSubscriptionCondition(userSubscription)) {

                  /!** skip onboarding **!/
                  if (user && (user && user.signUpFor && user.signUpFor.Action
                    && SKIP_ONBOARDING_ANSWERS.includes(user.signUpFor.Action.answer))) {
                    if (websites && websites.length === 1 && userSubscription?.trialClient && window.innerWidth < 991) {
                      const demoWebsite = websites.filter(website => website.isAiBuilderDemo);
                      if (!TypeUtils.isEmpty(demoWebsite)) {
                        this._router.navigate(['/single-page-demo']);
                        return resolve(false);
                      }
                      else {
                        return resolve(true);
                      }
                    }
                    else {
                      return resolve(true);
                    }
                  }

                  /!** redirect to onboarding **!/
                  if (!this._hasWebsiteCondition(user, websites)) {
                    return this._redirectToSetup(next.queryParams, reject);
                  }


                  /!** check if last option was AI recreation, if not resolve **!/
                  if (user && this._setupAiRecreateCondition(user, userLocalStorage) && websites && websites.length) {
                    return resolve(true);
                  }

                  if (websites && websites[0] && websites[0].id) {
                    if (!userLocalStorage.setupAiCreation && userLocalStorage.setupAiRecreation) {
                      return this._handleRecreatedWebsite(websites[0].id, userLocalStorage, next.queryParams, resolve, reject);
                    }
                    else if (userLocalStorage.setupAiCreation) {
                      return this._redirectToSetup(next.queryParams, reject);
                    }
                    else {
                      return resolve(true);
                    }
                  }

                  return resolve(true);
                }*/
                  return resolve(true);
              });
          }
        });
    });

  }

  private _trialSubscriptionCondition(userSubscription: UserSubscription): boolean {
    return !!(userSubscription.subscriptionId === Config.defaultStarterPlanId
      || userSubscription.freeBaseClient
      || userSubscription.trialClient
      || userSubscription.trialOptimizerClient);
  }

  private _allowedSubscriptionWithoutWebsiteCondition(userSubscription: UserSubscription): boolean {
    return !(
      this._trialSubscriptionCondition(userSubscription));
  }

  private _hasWebsiteCondition(user: User, websites: Website[]): boolean {
    return !!(user.websiteCreated || user.websiteConnected || (websites && websites.length));
  }

  /** check if user passed setup onboarding **/
  private _setupAiRecreateCondition(user: User, userLocalStorage: any): boolean {
    return !!(user && user.signUpFor && user.signUpFor.LastChosenOption && user.signUpFor.LastChosenOption.answer
      && ( !['ai-demo-section', 'ai-demo-section-after-signup', 'ai-demo-template', 'ai-demo-template-after-signup'].includes(user.signUpFor.LastChosenOption.answer)
        || (['ai-demo-section', 'ai-demo-section-after-signup', 'ai-demo-template', 'ai-demo-template-after-signup'].includes(user.signUpFor.LastChosenOption.answer)
          && !userLocalStorage.setupAiRecreation
          && !userLocalStorage.setupAiCreation)));
  }

  private _handleRecreatedWebsite(websiteId: number, userLocalStorage: any, queryParams: any, resolve: Function, reject: Function): void {
    this._store.dispatch(new fromStore.LoadLastBuilderTemplate({websiteId: websiteId, lastEntities: null}));
    this._store.select(fromStore.getLastBuilderTemplateLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getLastBuilderTemplate(websiteId))),
        first()
      )
      .subscribe(builderTemplate => {
        if (builderTemplate && builderTemplate.ever_recreated) {
          this._userService.setToLocal({...userLocalStorage, setupAiRecreation: null});
          return resolve(true);
        }
        else {
          return this._redirectToSetup(queryParams, reject);
        }
      });
  }

  private _redirectToSetup(queryParams: any, reject: Function): void {
    this._router.navigate(['/setup/builder-v2'], {queryParams});
    reject();
  }

  private hasReturnUrl(queryParams): boolean {
    if (this._pluginConnectService.pluginData && this._pluginConnectService.pluginData.returnUrl) {
      return !!this._pluginConnectService.pluginData.returnUrl;
    }
    else if (localStorage.getItem('pluginData')) {
      const pluginData = JSON.parse(localStorage.getItem('pluginData'));
      return !!pluginData.returnUrl;
    }
    else if (queryParams && queryParams.return_url) {
      return !!queryParams.return_url;
    }
    return false;
  }
}
